import { buildCompanyIndex, buildGridRows } from "./EnrichmentGrid.state";
import React, { useCallback, useMemo, useRef } from "react";
import { Checkbox } from "@mui/material";
import { SelectColumn, useRowSelection } from "react-data-grid";
import { isCompanyActionEligible } from "../../helper/companyEligibility";

// ALP-8: the design system's checkbox, not react-data-grid's stock one.
// Same selection wiring as SelectColumn's cell, including shift-click ranges.
export function EligibleSelectionCell({ row, tabIndex }) {
  const [isRowSelected, onRowSelectionChange] = useRowSelection();
  return (
    <Checkbox size="small" tabIndex={tabIndex} checked={isRowSelected}
      inputProps={{ 'aria-label': 'Select' }}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onRowSelectionChange({
        type: 'ROW', row, checked: event.target.checked, isShiftClick: event.nativeEvent.shiftKey,
      })} />
  );
}

const EMPTY_COMPANIES = [];

/**
 * `retainedCompanies` are selected companies from other pages of the same query. They
 * join the company and row indexes so paid actions and their confirmation cover the
 * whole selection, while `rows` (what the grid renders) stays the visible page.
 */
export function useCompanySelection(companies, requestedSelectedRows, onSelectedRowsChange,
  retainedCompanies = EMPTY_COMPANIES) {
  const companyById = useMemo(() => buildCompanyIndex([...retainedCompanies, ...companies]),
    [companies, retainedCompanies]);
  const rows = useMemo(() => buildGridRows(companies), [companies]);
  const rowById = useMemo(() => new Map(buildGridRows(Array.from(companyById.values()))
    .map((row) => [row.id, row])), [companyById]);
  // Selection can span pages. Remember observed ineligible IDs without treating
  // every company absent from the current page as unavailable.
  const unavailableIdsRef = useRef(new Set());
  const unavailableIds = useMemo(() => {
    const ids = new Set(unavailableIdsRef.current);
    companyById.forEach((company, id) => {
      if (isCompanyActionEligible(company)) ids.delete(id);
      else ids.add(id);
    });
    unavailableIdsRef.current = ids;
    return ids;
  }, [companyById]);
  const selectedRows = useMemo(() => new Set(Array.from(requestedSelectedRows)
    .filter((id) => id && !unavailableIds.has(id))), [requestedSelectedRows, unavailableIds]);
  const changeSelection = useCallback((selection) => {
    onSelectedRowsChange?.(new Set(Array.from(selection).filter((id) =>
      id && !unavailableIds.has(id))), rows.map((row) => row.id));
  }, [unavailableIds, onSelectedRowsChange, rows]);
  const selectionColumn = useMemo(() => ({
    ...SelectColumn,
    renderCell: (props) => props.row.readOnly
      ? <Checkbox disabled inputProps={{ 'aria-label': 'Read-only company' }} size="small" />
      : <EligibleSelectionCell row={props.row} tabIndex={props.tabIndex} />,
    renderHeaderCell: ({ tabIndex }) => {
      const eligible = rows.filter((row) => !row.readOnly);
      const allSelected = eligible.length > 0 && eligible.every((row) => selectedRows.has(row.id));
      return <Checkbox size="small" tabIndex={tabIndex} disabled={!eligible.length}
        inputProps={{ 'aria-label': 'Select All' }} checked={allSelected}
        onChange={() => changeSelection(allSelected ? new Set() : new Set(eligible.map((row) => row.id)))} />;
    },
  }), [rows, selectedRows, changeSelection]);

  return { companyById, rowById, rows, selectedRows, unavailableIds, changeSelection, selectionColumn };
}
